import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { Field, reduxForm, getFormSyncErrors, change } from 'redux-form';
import { IStore } from 'redux/IStore';
import { IOnboardingQuizFormData, IOnboardingQuiz, IOnboardingQuizAction } from 'models/onboardingquiz';
import { IModalAction } from 'models/modal';
import { ICountryCallingCodeDomainValue, ICropImageData, ICropImageObject, IImageData } from 'models/common';
import { PortalInputField, PortalSelectMenu } from 'components/FormControls';
import { ImageState, NewImageUpload } from 'components/FormControls/NewImageUpload';
import { phoneNumber, required } from 'helpers/validationattributes';
import { createDomainValueOptions } from 'helpers/common';
import { domainValuesMap } from 'redux/modules/domainvalues';
import { getOriginalImage } from 'redux/modules/image';
const { connect } = require('react-redux');
require('./style.css');

interface IProps {
	currentFormValues: IOnboardingQuizFormData;
	initialValues: IOnboardingQuizFormData;
	onboardingQuiz?: IOnboardingQuiz;
	dispatch?: any;
	editMode?: boolean;
	steps?: string[];
	userId?: string;
	countryCallingCodes?: ICountryCallingCodeDomainValue[];
	getCountryCallingCodeDomainValues?: () => any;
	onImgStateChange?: (state: ImageState) => void;
	getOriginalImage?: (imageId: string) => void;

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;
	formRef?: any;
	imgRef?: any;

	/* functions props */
	t?: (key: string, options?: any) => string;
	onSubmit?: (form: IOnboardingQuizFormData) => IOnboardingQuizAction;
	closeModal?: () => IModalAction;
	showModal?: (modalType: string, modalProps: any) => IModalAction;
	change: (field: string, value: any) => void;
	gotoStep?: (step: number) => IOnboardingQuizAction;
	updateProfilePicture: (image: ICropImageData) => IOnboardingQuizAction;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class UserInfoForm extends React.Component<IProps, any> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.changeImageData = this.changeImageData.bind(this);
		this.changeImageCrop = this.changeImageCrop.bind(this);
		this.confirmImage = this.confirmImage.bind(this);
	}

	componentDidMount(): void {
		this.props.getCountryCallingCodeDomainValues();
		this.props.getOriginalImage(this.props.currentFormValues.pictureId);
	}

	private onSubmit(form: IOnboardingQuizFormData) {
		this.props.onSubmit(form);
	}

	private changeImageData(image: IImageData) {
		return new Promise<void>((resolve) => {
			this.props.change('picture', image.href);
			this.props.change('pictureId', image.id);
			resolve();
		});
	}

	private changeImageCrop(cropObject: ICropImageObject) {
		return this.props.change('pictureCrop', cropObject);
	}

	private confirmImage() {
		const { pictureCrop, pictureId } = this.props.currentFormValues;
		if (!pictureId) {
			return;
		}
		const cropData: ICropImageData = {
			...pictureCrop,
			id: pictureId,
			entityReferenceId: this.props.userId,
			entityType: c.NODE_TYPE_USER,
		};
		this.props.updateProfilePicture(cropData);
	}

	public render() {
		const { handleSubmit, t, formRef, countryCallingCodes } = this.props;
		const { picture, pictureId, pictureCrop } = this.props.currentFormValues;

		const callingCodes = createDomainValueOptions(t, 'DV_CountryCallingCodes_', countryCallingCodes);

		function createMarkup() {
			return { __html: t('OnboardingQuiz_StepUserInfo_Title') };
		}

		return (
			<div className="quizUserInfo">
				<div className="modalBodyHeader">
					<h1 dangerouslySetInnerHTML={createMarkup()}></h1>
				</div>
				<div className="modalBodyContent">
					<div className="wizardContent narrow">
						<form onSubmit={handleSubmit(this.onSubmit)} ref={formRef}>
							<div className="row inputFieldsWrapper">
								<div className="col-md-6">
									<Field
										validate={[required]}
										name="givenName"
										component={PortalInputField}
										type="text"
										label={t('OnboardingQuiz_StepUserInfo_Label_GivenName')}
									/>
								</div>
								<div className="col-md-6">
									<Field
										validate={[required]}
										name="familyName"
										component={PortalInputField}
										type="text"
										label={t('OnboardingQuiz_StepUserInfo_Label_FamilyName')}
									/>
								</div>
								<div className="col-md-4">
									<Field
										name={'callingCodeKey'}
										component={PortalSelectMenu}
										type="text"
										items={callingCodes}
										withImages={true}
										multi={false}
										validate={[required]}
										isRequired={true}
										label={t('BSPortal_Common_Phone_Prefix')}
									/>
								</div>
								<div className="col-md-8">
									<Field
										name={'phoneNumber'}
										component={PortalInputField}
										type="text"
										label={t('PhoneNumber_Label')}
										validate={[required, phoneNumber]}
									/>
								</div>
							</div>
							<hr />
							<NewImageUpload
								title={t('User_UserCard_ProfilePicture')}
								imageId={pictureId}
								imageUrl={picture}
								imageCrop={pictureCrop}
								onChangeCropData={this.changeImageCrop}
								onChangeImageData={this.changeImageData}
								onConfirm={this.confirmImage}
								onImgStateChange={this.props.onImgStateChange}
								currentFormValues={this.props.currentFormValues}
							/>
							<div className="spacer" />
						</form>
					</div>
				</div>
			</div>
		);
	}
}

let UserInfo = reduxForm({
	form: c.FORM_NAME_ONBOARDING_QUIZ,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
})(UserInfoForm);

UserInfo = connect(
	(state: IStore) => {
		let currentFormValues: IOnboardingQuizFormData = null;
		if (state.form[c.FORM_NAME_ONBOARDING_QUIZ] && state.form[c.FORM_NAME_ONBOARDING_QUIZ].values) {
			currentFormValues = state.form[c.FORM_NAME_ONBOARDING_QUIZ].values;
		} else {
			currentFormValues = state.onboardingQuiz.formData;
		}

		return {
			onboardingQuiz: state.onboardingQuiz,
			currentFormValues,
			formErrors: getFormSyncErrors(c.FORM_NAME_ONBOARDING_QUIZ)(state),
			userId: state.userPreferences.id,
			countryCallingCodes: state.domainValues.countryCallingCodes,
		};
	},
	(dispatch) => ({
		change: (field: string, value: string) => dispatch(change(field, value)),
		getCountryCallingCodeDomainValues: () => dispatch(domainValuesMap.countryCallingCodes()),
		getOriginalImage: (imageId: string) => dispatch(getOriginalImage(imageId, c.FORM_NAME_ONBOARDING_QUIZ)),
	}),
)(UserInfo);

export { UserInfo };
